import "./ManageCars.css";
import {
  fetchCars,
  setCarInactive,
  removeCar,
  activateCar,
  addCar,
} from "./carsSlice";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

function ManageCars() {
  const dispatch = useDispatch();
  const cars = useSelector((state) => state.cars.carsList);
  const [newCarName, setNewCarName] = useState("");
  const [formFeedback, setFormFeedback] = useState({
    status: "idle",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Dispatch the fetchCars action when the component mounts
    dispatch(fetchCars());
  }, [dispatch]);

  const handleSetCarInactive = (carId) => {
    dispatch(setCarInactive(carId));
  };

  const handleRemoveCar = (carId) => {
    dispatch(removeCar(carId));
  };

  const handleSetCarActive = (carId) => {
    dispatch(activateCar(carId));
  };

  const handleAddCar = async (event) => {
    event.preventDefault();
    const trimmedName = newCarName.trim();

    if (!trimmedName) {
      setFormFeedback({
        status: "error",
        message: "Enter a car name before adding it to the fleet.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await dispatch(
        addCar({ carName: trimmedName, status: "active", capacity: 4 })
      ).unwrap();

      setFormFeedback({
        status: "success",
        message: `“${trimmedName}” was added to your fleet.`,
      });
      setNewCarName("");
    } catch (error) {
      setFormFeedback({
        status: "error",
        message: "We couldn't add that car. Try again in a moment.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="manage-cars-container">
      <h1>Manage Cars</h1>
      <form className="add-car-form" onSubmit={handleAddCar} noValidate>
        <label className="add-car-form__label" htmlFor="new-car-name">
          Car name
        </label>
        <div className="add-car-form__controls">
          <input
            id="new-car-name"
            type="text"
            placeholder="e.g. Compact Sedan"
            className="input-field"
            value={newCarName}
            onChange={(event) => {
              setNewCarName(event.target.value);
              if (formFeedback.status !== "idle") {
                setFormFeedback({ status: "idle", message: "" });
              }
            }}
            aria-describedby={
              formFeedback.message ? "add-car-feedback" : undefined
            }
          />

          <button type="submit" className="add-button" disabled={isSubmitting}>
            {isSubmitting ? "Adding…" : "Add Car"}
          </button>
        </div>
        {formFeedback.message && (
          <p
            id="add-car-feedback"
            className={`form-feedback${
              formFeedback.status !== "idle"
                ? ` form-feedback--${formFeedback.status}`
                : ""
            }`}
            role={formFeedback.status === "error" ? "alert" : "status"}
            aria-live={
              formFeedback.status === "error" ? "assertive" : "polite"
            }
          >
            {formFeedback.message}
          </p>
        )}
      </form>
      <ul className="car-list">
        {cars.map((car) => (
          <li key={car.id} className="car-card">
            <div className="car-details">
              <div className="car-primary">
                <span className="car-name">{car.carName}</span>
                {car.status && (
                  <span
                    className={`car-status car-status--${car.status
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                  >
                    {car.status}
                  </span>
                )}
              </div>
              <div className="car-meta">
                <span>{car.capacity} seats</span>
                {car.id && <span className="car-id">ID: {car.id}</span>}
              </div>
            </div>
            <div className="car-actions">
              <button
                onClick={() => handleRemoveCar(car.id)}
                className="remove-button"
              >
                Remove
              </button>
              <button
                onClick={() => handleSetCarInactive(car.id)}
                className="inactive-button"
              >
                Set Inactive
              </button>
              <button
                onClick={() => handleSetCarActive(car.id)}
                className="active-button"
              >
                Set Active
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default ManageCars;
